import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

// Phase 3.2: API base for raw image src URLs (cover/preview)
const API = `${BACKEND_URL}/api`;

const AdminMagazineManager = () => {
  const [user, setUser] = useState(null);
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    issue_date: "",
    description: "",
    price: "5.99"
  });
  const [featuredArtists, setFeaturedArtists] = useState([{ name: "", category: "" }]);
  const [files, setFiles] = useState({
    cover_image: null,
    preview_images: [],
    full_pdf: null
  });
  const [published, setPublished] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [issuesRes, statsRes] = await Promise.all([
        api.get(`/admin/magazine/issues`),
        api.get(`/admin/magazine/stats`)
      ]);
      setIssues(issuesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get(`/auth/me`);
      if (response.data.role !== "admin" && response.data.role !== "moderator") {
        toast.error("Admin access required");
        navigate('/dashboard');
        return;
      }
      setUser(response.data);
      await fetchData();
    } catch (error) {
      navigate('/login');
    }
  }, [navigate, fetchData]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const addArtist = () => {
    setFeaturedArtists([...featuredArtists, { name: "", category: "" }]);
  };

  const removeArtist = (index) => {
    setFeaturedArtists(featuredArtists.filter((_, i) => i !== index));
  };

  const updateArtist = (index, field, value) => {
    const updated = [...featuredArtists];
    updated[index][field] = value;
    setFeaturedArtists(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!files.cover_image || files.preview_images.length === 0 || !files.full_pdf) {
      toast.error("Please upload all required files");
      return;
    }

    setCreating(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("issue_date", formData.issue_date);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("price", formData.price);
      formDataToSend.append("featured_artists_json", JSON.stringify(featuredArtists));
      formDataToSend.append("published", published);
      formDataToSend.append("cover_image", files.cover_image);
      formDataToSend.append("full_pdf", files.full_pdf);
      
      files.preview_images.forEach((preview) => {
        formDataToSend.append("preview_images", preview);
      });

      await api.post(`/admin/magazine/issues`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Magazine issue created successfully!");
      setShowCreateForm(false);
      resetForm();
      await fetchData();
    } catch (error) {
      toast.error("Failed to create issue", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", issue_date: "", description: "", price: "5.99" });
    setFeaturedArtists([{ name: "", category: "" }]);
    setFiles({ cover_image: null, preview_images: [], full_pdf: null });
    setPublished(false);
  };

  const togglePublish = async (issueId, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append("published", !currentStatus);

      await api.patch(`/admin/magazine/issues/${issueId}`, formData);

      toast.success(`Issue ${!currentStatus ? 'published' : 'unpublished'} successfully`);
      await fetchData();
    } catch (error) {
      toast.error("Failed to update issue");
    }
  };

  // ============ Phase 3.2: Inline edit ============
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({ title: "", issue_date: "", description: "", price: "" });
  const [editingFeatured, setEditingFeatured] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [replacingCover, setReplacingCover] = useState(false);

  const startEditing = (issue) => {
    setEditingId(issue.issue_id);
    setEditingData({
      title: issue.title || "",
      issue_date: issue.issue_date || "",
      description: issue.description || "",
      price: String(issue.price ?? ""),
    });
    setEditingFeatured(issue.featured_artists || [{ name: "", category: "" }]);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingData({ title: "", issue_date: "", description: "", price: "" });
    setEditingFeatured([]);
  };

  const saveEditing = async (issueId) => {
    if (savingEdit) return; // prevent duplicate saves
    setSavingEdit(true);
    try {
      const fd = new FormData();
      // Phase 3.2: only append fields with values; backend treats blanks as no-op
      if (editingData.title?.trim()) fd.append("title", editingData.title.trim());
      if (editingData.issue_date?.trim()) fd.append("issue_date", editingData.issue_date.trim());
      if (editingData.description?.trim()) fd.append("description", editingData.description.trim());
      const priceStr = String(editingData.price ?? "").trim();
      if (priceStr !== "") {
        const priceNum = Number(priceStr);
        if (Number.isNaN(priceNum) || priceNum < 0) {
          toast.error("Invalid price", { description: "Price must be a positive number" });
          setSavingEdit(false);
          return;
        }
        fd.append("price", String(priceNum));
      }
      const cleanFeatured = (editingFeatured || []).filter(a => a && a.name);
      fd.append("featured_artists_json", JSON.stringify(cleanFeatured));
      await api.patch(`/admin/magazine/issues/${issueId}`, fd);
      toast.success("Issue updated");
      cancelEditing();
      await fetchData();
    } catch (error) {
      toast.error("Failed to save", {
        description: error.response?.data?.detail || "Try again",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleReplaceCover = async (issueId, file) => {
    if (!file) return;
    setReplacingCover(true);
    try {
      const fd = new FormData();
      fd.append("cover_image", file);
      await api.post(`/admin/magazine/issues/${issueId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover replaced");
      await fetchData();
    } catch (error) {
      toast.error("Failed to replace cover");
    } finally {
      setReplacingCover(false);
    }
  };

  const handleAddPreviews = async (issueId, fileList) => {
    if (!fileList || fileList.length === 0) return;
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((f) => fd.append("preview_images", f));
      await api.post(`/admin/magazine/issues/${issueId}/previews`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Previews added");
      await fetchData();
    } catch (error) {
      toast.error("Failed to add previews");
    }
  };

  const handleRemovePreview = async (issueId, filename) => {
    if (!window.confirm("Remove this preview image?")) return;
    try {
      await api.delete(`/admin/magazine/issues/${issueId}/previews/${encodeURIComponent(filename)}`);
      toast.success("Preview removed");
      await fetchData();
    } catch (error) {
      toast.error("Failed to remove preview");
    }
  };

  const handleDeleteIssue = async (issueId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/magazine/issues/${issueId}`);
      toast.success("Issue deleted");
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1e' }}>
      {/* Header */}
      <div className="border-b border-gray-800" style={{ background: '#0f0f11' }}>
        <div className="container-custom">
          <div className="flex items-center justify-between py-4">
            <h1 className="text-2xl font-bold text-white" data-testid="admin-magazine-title">
              Magazine Manager
            </h1>
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="text-white border-gray-700"
              data-testid="back-to-admin"
            >
              Back to Admin
            </Button>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
              <div className="text-gray-400 text-sm mb-1">Total Issues</div>
              <div className="text-3xl font-bold text-white">{stats.total_issues}</div>
            </Card>
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
              <div className="text-gray-400 text-sm mb-1">Published</div>
              <div className="text-3xl font-bold text-green-400">{stats.published_issues}</div>
            </Card>
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
              <div className="text-gray-400 text-sm mb-1">Total Sales</div>
              <div className="text-3xl font-bold text-purple-400">{stats.total_purchases}</div>
            </Card>
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
              <div className="text-gray-400 text-sm mb-1">Revenue</div>
              <div className="text-3xl font-bold text-gold">${stats.total_revenue.toFixed(2)}</div>
            </Card>
          </div>
        )}

        {/* Create Button */}
        {!showCreateForm && (
          <div className="mb-8">
            <Button
              onClick={() => setShowCreateForm(true)}
              className="submit-music-btn"
              data-testid="create-issue-btn"
            >
              Create New Issue
            </Button>
          </div>
        )}

        {/* Create Form */}
        {showCreateForm && (
          <Card className="p-8 mb-8" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
            <h2 className="text-2xl font-bold text-white mb-6">Create Magazine Issue</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-300">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="form-input mt-1"
                    data-testid="input-title"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Issue Date *</Label>
                  <Input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    required
                    className="form-input mt-1"
                    data-testid="input-date"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  className="form-textarea mt-1"
                  data-testid="input-description"
                />
              </div>

              <div>
                <Label className="text-gray-300">Price (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  className="form-input mt-1"
                  data-testid="input-price"
                />
              </div>

              {/* Featured Artists */}
              <div>
                <Label className="text-gray-300 mb-2 block">Featured Artists</Label>
                {featuredArtists.map((artist, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <Input
                      placeholder="Artist Name"
                      value={artist.name}
                      onChange={(e) => updateArtist(idx, 'name', e.target.value)}
                      className="form-input"
                      data-testid={`artist-name-${idx}`}
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Category (e.g., Cover Artist)"
                        value={artist.category}
                        onChange={(e) => updateArtist(idx, 'category', e.target.value)}
                        className="form-input flex-1"
                        data-testid={`artist-category-${idx}`}
                      />
                      {idx > 0 && (
                        <Button
                          type="button"
                          onClick={() => removeArtist(idx)}
                          variant="outline"
                          className="text-red-400 border-red-500"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={addArtist}
                  variant="outline"
                  className="text-purple-400 border-purple-500"
                  data-testid="add-artist-btn"
                >
                  + Add Artist
                </Button>
              </div>

              {/* File Uploads */}
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Cover Image (JPG/PNG) *</Label>
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => setFiles({ ...files, cover_image: e.target.files[0] })}
                    required
                    className="form-input mt-1"
                    data-testid="input-cover"
                  />
                  {files.cover_image && (
                    <p className="text-sm text-green-400 mt-1">✓ {files.cover_image.name}</p>
                  )}
                </div>

                <div>
                  <Label className="text-gray-300">Preview Pages (3-5 images) *</Label>
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    multiple
                    onChange={(e) => setFiles({ ...files, preview_images: Array.from(e.target.files) })}
                    required
                    className="form-input mt-1"
                    data-testid="input-previews"
                  />
                  {files.preview_images.length > 0 && (
                    <p className="text-sm text-green-400 mt-1">✓ {files.preview_images.length} files selected</p>
                  )}
                </div>

                <div>
                  <Label className="text-gray-300">Full Magazine PDF *</Label>
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFiles({ ...files, full_pdf: e.target.files[0] })}
                    required
                    className="form-input mt-1"
                    data-testid="input-pdf"
                  />
                  {files.full_pdf && (
                    <p className="text-sm text-green-400 mt-1">✓ {files.full_pdf.name}</p>
                  )}
                </div>
              </div>

              {/* Publish Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="published"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  className="w-5 h-5"
                  data-testid="input-published"
                />
                <Label htmlFor="published" className="text-gray-300">
                  Publish immediately
                </Label>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={creating}
                  className="submit-music-btn"
                  data-testid="submit-create-btn"
                >
                  {creating ? "Creating..." : "Create Issue"}
                </Button>
                <Button
                  type="button"
                  onClick={() => { setShowCreateForm(false); resetForm(); }}
                  variant="outline"
                  className="text-white border-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Issues List */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6" data-testid="issues-list-title">
            All Issues ({issues.length})
          </h2>
          
          <div className="space-y-4">
            {issues.map((issue) => (
              <Card
                key={issue.issue_id}
                className="p-6"
                style={{ background: 'rgba(42, 26, 58, 0.5)', borderColor: 'rgba(138, 43, 226, 0.2)' }}
                data-testid={`issue-${issue.slug}`}
              >
                <div className="flex gap-6">
                  <div className="relative">
                    <img
                      src={`${API}/magazine/public/cover/${issue.cover_image}`}
                      alt={issue.title}
                      className="w-24 h-auto rounded"
                    />
                    {editingId === issue.issue_id && (
                      <label className="absolute inset-0 flex items-center justify-center bg-black/70 rounded text-xs text-white cursor-pointer hover:bg-black/90">
                        {replacingCover ? 'Uploading…' : 'Replace'}
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          className="hidden"
                          disabled={replacingCover}
                          onChange={(e) => handleReplaceCover(issue.issue_id, e.target.files?.[0])}
                          data-testid={`replace-cover-${issue.slug}`}
                        />
                      </label>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        {editingId === issue.issue_id ? (
                          <Input
                            value={editingData.title}
                            onChange={(e) => setEditingData({ ...editingData, title: e.target.value })}
                            className="form-input mb-1"
                            data-testid={`edit-title-${issue.slug}`}
                          />
                        ) : (
                          <h3 className="text-xl font-bold text-white">{issue.title}</h3>
                        )}
                        {editingId === issue.issue_id ? (
                          <Input
                            type="date"
                            value={editingData.issue_date?.slice(0, 10) || ''}
                            onChange={(e) => setEditingData({ ...editingData, issue_date: e.target.value })}
                            className="form-input text-sm"
                            data-testid={`edit-date-${issue.slug}`}
                          />
                        ) : (
                          <p className="text-gray-400 text-sm">
                            {issue.issue_date ? new Date(issue.issue_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : '—'}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 items-start">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          issue.published 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {issue.published ? 'Published' : 'Draft'}
                        </span>
                        {editingId === issue.issue_id ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editingData.price}
                            onChange={(e) => setEditingData({ ...editingData, price: e.target.value })}
                            className="form-input w-24"
                            data-testid={`edit-price-${issue.slug}`}
                          />
                        ) : (
                          <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gold/20 text-gold">
                            ${typeof issue.price === 'number' ? issue.price.toFixed(2) : issue.price}
                          </span>
                        )}
                      </div>
                    </div>
                    {editingId === issue.issue_id ? (
                      <Textarea
                        value={editingData.description}
                        onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                        className="form-textarea mb-4"
                        rows={3}
                        data-testid={`edit-description-${issue.slug}`}
                      />
                    ) : (
                      <p className="text-gray-300 mb-4">{issue.description}</p>
                    )}

                    {/* Preview management when editing */}
                    {editingId === issue.issue_id && (
                      <div className="mb-4 p-3 rounded bg-black/30 border border-gray-800" data-testid={`edit-featured-artists-${issue.slug}`}>
                        <p className="text-xs text-gray-400 mb-2">Inside This Issue — Featured Artists</p>
                        <div className="space-y-2">
                          {(editingFeatured || []).map((a, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={a.name || ''}
                                onChange={(e) => {
                                  const next = [...editingFeatured];
                                  next[idx] = { ...next[idx], name: e.target.value };
                                  setEditingFeatured(next);
                                }}
                                placeholder="Artist name"
                                className="flex-1 px-3 py-1.5 rounded bg-black/40 text-white border border-gray-700 text-sm"
                                data-testid={`featured-name-${idx}`}
                              />
                              <input
                                type="text"
                                value={a.category || ''}
                                onChange={(e) => {
                                  const next = [...editingFeatured];
                                  next[idx] = { ...next[idx], category: e.target.value };
                                  setEditingFeatured(next);
                                }}
                                placeholder="Category (e.g. Cover Story)"
                                className="flex-1 px-3 py-1.5 rounded bg-black/40 text-white border border-gray-700 text-sm"
                                data-testid={`featured-category-${idx}`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingFeatured((prev) => prev.filter((_, i) => i !== idx));
                                }}
                                className="px-2 py-1 rounded text-red-300 hover:text-red-100 hover:bg-red-500/20"
                                title="Remove"
                                data-testid={`featured-remove-${idx}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setEditingFeatured([...(editingFeatured || []), { name: '', category: '' }])}
                            className="text-sm text-purple-300 hover:text-white"
                            data-testid={`featured-add-artist-${issue.slug}`}
                          >
                            + Add artist
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Preview management when editing */}
                    {editingId === issue.issue_id && (
                      <div className="mb-4 p-3 rounded bg-black/30 border border-gray-800">
                        <p className="text-xs text-gray-400 mb-2">Preview Images</p>
                        <div className="flex flex-wrap gap-2">
                          {(issue.preview_images || []).map((p) => (
                            <div key={p} className="relative">
                              <img
                                src={`${API}/magazine/public/preview/${p}`}
                                alt="preview"
                                className="w-16 h-20 object-cover rounded"
                              />
                              <button
                                onClick={() => handleRemovePreview(issue.issue_id, p)}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 rounded-full text-white text-xs"
                                data-testid={`remove-preview-${p}`}
                              >×</button>
                            </div>
                          ))}
                          <label className="w-16 h-20 flex items-center justify-center border-2 border-dashed border-gray-600 rounded text-gray-400 hover:text-white cursor-pointer text-xl">
                            +
                            <input
                              type="file"
                              multiple
                              accept=".jpg,.jpeg,.png,.webp"
                              className="hidden"
                              onChange={(e) => handleAddPreviews(issue.issue_id, e.target.files)}
                              data-testid={`add-previews-${issue.slug}`}
                            />
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 flex-wrap">
                      {editingId === issue.issue_id ? (
                        <>
                          <Button
                            onClick={() => saveEditing(issue.issue_id)}
                            disabled={savingEdit}
                            size="sm"
                            className="submit-music-btn"
                            data-testid={`save-edit-${issue.slug}`}
                          >
                            {savingEdit ? 'Saving…' : 'Save'}
                          </Button>
                          <Button onClick={cancelEditing} size="sm" variant="outline" className="text-white border-gray-700">
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => startEditing(issue)}
                            size="sm"
                            variant="outline"
                            className="text-white border-gray-700"
                            data-testid={`edit-issue-${issue.slug}`}
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => togglePublish(issue.issue_id, issue.published)}
                            size="sm"
                            variant={issue.published ? "outline" : "default"}
                            className={issue.published ? "text-white border-gray-700" : "submit-music-btn"}
                            data-testid={`toggle-publish-${issue.slug}`}
                          >
                            {issue.published ? 'Unpublish' : 'Publish'}
                          </Button>
                          <Button
                            onClick={() => handleDeleteIssue(issue.issue_id, issue.title)}
                            size="sm"
                            variant="outline"
                            className="text-red-300 border-red-500/40 hover:bg-red-500/20"
                            data-testid={`delete-issue-${issue.slug}`}
                          >
                            Delete
                          </Button>
                          <a
                            href={`/magazine/issue/${issue.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button size="sm" variant="outline" className="text-purple-400 border-purple-500">
                              View Public Page
                            </Button>
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMagazineManager;
